import * as ngCore from '@angular/core'
import * as ngRouter from '@angular/router'

import { RouteParam } from './route-param'

// Route data key used to declare which route params define a reuse boundary.
// When one of these params changes, Angular must destroy the existing route
// instance so route-bound providers can be torn down.
export const ROUTE_BOUND_PARAMS = 'routeBoundParams'

/**
 * Keeps route-bound state alive only for the route params that own it.
 *
 * Angular normally reuses a route when the same route config remains active,
 * even if important params changed. That is useful for navigation inside the
 * same logical route boundary, but wrong when a param change represents a new
 * owner for route-bound providers. For example, switching apps should destroy
 * the previous app route injector so its route-bound state can clean itself up.
 *
 * This strategy does not implement detached-route caching. It only customizes
 * whether Angular may reuse the currently activated route instance.
 */
@ngCore.Injectable()
export class RouteBoundReuseStrategy implements ngRouter.RouteReuseStrategy {
  /**
   * Determines whether Angular should remove the active route tree from the
   * outlet and keep it cached for later.
   *
   * We never detach route trees because detached caching would keep the route
   * injector alive after navigation, delaying provider cleanup for route-bound
   * services such as app-level state.
   */
  public shouldDetach(_: ngRouter.ActivatedRouteSnapshot): boolean {
    return false
  }

  /**
   * Stores a detached route tree for later reuse.
   *
   * This is a no-op because {@link shouldDetach} always returns false, so
   * Angular will never hand us a detached route tree to store.
   */
  public store(
    _: ngRouter.ActivatedRouteSnapshot,
    __: ngRouter.DetachedRouteHandle | null,
  ): void {}

  /**
   * Determines whether Angular should reattach a previously cached route tree.
   *
   * We never reattach cached route trees because this strategy intentionally
   * does not cache any.
   */
  public shouldAttach(_: ngRouter.ActivatedRouteSnapshot): boolean {
    return false
  }

  /**
   * Retrieves a cached route tree for the requested route.
   *
   * No cached handles exist, so there is never anything to retrieve.
   */
  public retrieve(
    _: ngRouter.ActivatedRouteSnapshot,
  ): ngRouter.DetachedRouteHandle | null {
    return null
  }

  /**
   * Determines whether Angular can keep using the currently active route
   * instance for the next navigation.
   *
   * For routes with {@link ROUTE_BOUND_PARAMS}, all declared params must remain
   * equal. This lets child navigations inside the same route-bound owner reuse
   * providers, while owner changes recreate the route injector and clean up
   * route-bound state.
   */
  public shouldReuseRoute(
    future: ngRouter.ActivatedRouteSnapshot,
    curr: ngRouter.ActivatedRouteSnapshot,
  ): boolean {
    // Different route definitions should use Angular's normal destroy/create
    // behavior.
    if (future.routeConfig !== curr.routeConfig) {
      return false
    }

    // Routes without explicit route-bound params follow Angular's default
    // behavior: same route config means the route instance can be reused.
    const routeBoundParams = future.routeConfig?.data?.[
      ROUTE_BOUND_PARAMS
    ] as readonly RouteParam[] | undefined

    if (!routeBoundParams?.length) {
      return true
    }

    // For route-bound routes, reuse only while every declared owner param is
    // stable. Example: the app route may survive page changes, but must be
    // recreated when `appAlias` changes.
    return routeBoundParams.every(
      param => getParam(future, param) === getParam(curr, param),
    )
  }
}

// Params can be declared on parent routes, so walk from the leaf route upward
// and return the closest value for the requested param.
function getParam(
  route: ngRouter.ActivatedRouteSnapshot,
  param: RouteParam,
): string | undefined {
  for (let idx = route.pathFromRoot.length - 1; idx >= 0; idx--) {
    const value = route.pathFromRoot[idx].paramMap.get(param)
    if (value !== null) {
      return value
    }
  }
  return undefined
}
